import { Helmet } from 'react-helmet-async';
import { Fragment, type ReactNode } from 'react';

const TERMS_OF_USE_TEXT = `## PREÂMBULO

O presente instrumento, denominado "Termos e Condições Gerais de Uso — Usuário" ("**Termos**"), regula o acesso, o cadastro e a utilização da plataforma digital **Plumaa**, de titularidade de **ARAUJO & SILVA LTDA**, pessoa jurídica de direito privado, inscrita no CNPJ sob o nº **65.321.886/0001-10**, com sede na Rua Pais Leme, nº 215, Conj. 1713, Bairro Pinheiros, São Paulo/SP, CEP 05.424-150, e nome fantasia **PLUMAA** (doravante "**Plumaa**").

Estes Termos, em conjunto com a **Política de Privacidade** da Plumaa, constituem um contrato eletrônico vinculante entre a Plumaa e o **Usuário**. Ao acessar, cadastrar-se ou utilizar qualquer funcionalidade da Plataforma, o Usuário declara, de forma livre, informada e inequívoca, que leu, compreendeu e concorda integralmente com todas as disposições aqui previstas.

**Caso o Usuário não concorde com qualquer disposição destes Termos, deverá abster-se, imediatamente, de acessar ou utilizar a Plataforma.**

## CLÁUSULA 1 — DEFINIÇÕES

1.1. Para os fins destes Termos, as expressões abaixo, quando iniciadas em letra maiúscula, terão os seguintes significados:

**Plataforma** ou **Plumaa**: ambiente digital (website, aplicativos e interfaces correlatas) disponibilizado pela Plumaa, que atua como intermediadora tecnológica entre Usuários e Profissionais da Saúde.

**Usuário**, **Paciente** ou **Você**: pessoa física, maior de 18 (dezoito) anos ou emancipada, em pleno gozo de sua capacidade civil, que realiza cadastro e utiliza os serviços da Plataforma de forma particular, na modalidade direta ao consumidor (B2C), sem vínculo com plano empresarial.

**Colaborador**: pessoa física vinculada a uma Empresa Contratante da Plumaa, cujo acesso à Plataforma se dá por meio de plano empresarial (B2B2C). As regras aplicáveis ao Colaborador estão previstas em instrumento específico ("Termos de Uso do Colaborador"), não se confundindo com a figura do Usuário ora definida.

**Profissional da Saúde** ou **Profissional**: pessoa física ou jurídica, regularmente inscrita e habilitada em seu respectivo conselho de classe (Conselho Regional de Psicologia — CRP, Conselho Regional de Medicina — CRM, ou Conselho Regional de Nutrição — CRN), que se cadastra na Plataforma para prestar serviços de teleatendimento em saúde mental a Usuários e Colaboradores.

**Teleatendimento** ou **Teleconsulta**: modalidade de prestação de serviço em saúde realizada à distância, por videochamada integrada à Plataforma (Google Meet), nos termos da Lei nº 14.510/2022 e das resoluções dos conselhos profissionais aplicáveis.

**Conta**: acesso individual e intransferível do Usuário à Plataforma, protegido por login e senha de uso pessoal.

**LGPD**: Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018).

**CDC**: Código de Defesa do Consumidor (Lei nº 8.078/1990).

## CLÁUSULA 2 — ADESÃO AOS TERMOS

2.1. A utilização da Plataforma pelo Usuário constitui manifestação livre, informada e inequívoca de aceitação integral destes Termos e da Política de Privacidade da Plumaa.

2.2. A Plumaa poderá, a qualquer tempo e a seu exclusivo critério, modificar, adicionar ou suprimir cláusulas destes Termos, com o objetivo de adequá-los à legislação aplicável, à evolução tecnológica da Plataforma ou à melhoria dos serviços prestados.

2.3. Nas hipóteses de alteração **substancial**, o Usuário será notificado, com antecedência razoável, por meio do e-mail cadastrado ou por aviso ostensivo na Plataforma. A continuidade do uso, após a entrada em vigor das novas disposições, será interpretada como aceitação integral das alterações.

2.4. Estes Termos estarão permanentemente disponíveis para consulta no domínio oficial da Plumaa.

## CLÁUSULA 3 — NATUREZA DOS SERVIÇOS E LIMITAÇÕES

3.1. **Natureza de Intermediação Tecnológica.** A Plumaa é **plataforma digital de intermediação tecnológica** em saúde, cuja finalidade é conectar Usuários a Profissionais da Saúde autônomos e independentes, por meio de infraestrutura de software. **A Plumaa NÃO presta serviços médicos, psicológicos, nutricionais, terapêuticos, assistenciais ou de qualquer outra natureza clínica**, tampouco atua como prestador direto de cuidados em saúde, operadora de plano de saúde, seguradora, clínica, consultório ou centro médico.

3.2. **Autonomia do Profissional.** Os Profissionais da Saúde cadastrados atuam como **profissionais liberais e independentes**, sendo responsáveis exclusivos pela execução técnica, ética e legal dos atendimentos. Não existe vínculo empregatício, societário, de subordinação, associação ou representação entre os Profissionais e a Plumaa.

3.3. **Caráter Eletivo e Não Emergencial.** A Plataforma destina-se **exclusivamente a atendimentos eletivos** em saúde, não sendo apta a suportar situações de emergência, urgência, crise aguda, risco iminente à vida, ideação suicida, automutilação, surto psicótico, violência ou quaisquer outras hipóteses que demandem intervenção presencial imediata.

3.3.1. Em qualquer situação de risco à vida ou à integridade física ou psíquica, o Usuário deverá buscar, **imediatamente**, um dos seguintes serviços:
- **SAMU** — 192
- **Polícia Militar** — 190
- **Bombeiros** — 193
- **Centro de Valorização da Vida (CVV)** — 188
- Pronto-socorro, UPA ou hospital mais próximo

3.3.2. A Plumaa **não se responsabiliza**, sob qualquer hipótese, por eventos, danos, lesões, agravamentos clínicos ou óbitos decorrentes da omissão do Usuário em buscar atendimento presencial ou serviço de emergência adequado nas situações descritas nesta cláusula.

3.4. **Ausência de Relação de Consumo com o Profissional.** A relação contratual de prestação de serviços em saúde forma-se **diretamente entre o Usuário e o Profissional da Saúde escolhido**, cabendo à Plumaa unicamente a disponibilização da infraestrutura tecnológica de intermediação, agendamento e processamento de pagamentos.

## CLÁUSULA 4 — CADASTRO E CONTA

4.1. Para utilizar integralmente a Plataforma, o Usuário deverá realizar cadastro, fornecendo, no mínimo: nome completo, e-mail, telefone celular, CPF, data de nascimento e senha de acesso.

4.2. O Usuário declara e garante que:

a) é maior de 18 (dezoito) anos ou emancipado, em pleno gozo de sua capacidade civil;

b) todas as informações fornecidas no cadastro são **verdadeiras, exatas, completas e atuais**, comprometendo-se a mantê-las atualizadas;

c) utiliza a Plataforma para **finalidade pessoal e individual**, sendo vedado o agendamento de consultas em benefício de terceiros, ressalvadas as hipóteses legais expressas e autorizadas pela Plumaa;

d) não criará mais de um cadastro, nem utilizará identidade de terceiros, documentos falsos ou adulterados.

4.3. A Plumaa poderá, a qualquer tempo e a seu exclusivo critério, solicitar documentos comprobatórios, condicionar o acesso à Plataforma à validação dos dados, bem como suspender ou encerrar o cadastro em caso de inveracidade, inexatidão ou indício de fraude.

4.4. **Responsabilidade pela Conta.** O Usuário é o único responsável pelo sigilo e pela segurança de suas credenciais de acesso (login e senha). **Todas as ações realizadas na Plataforma sob a conta do Usuário serão presumidamente imputadas a ele**, cabendo-lhe arcar com as consequências civis, administrativas e criminais decorrentes.

4.4.1. É vedado ceder, alugar, comercializar, transferir ou compartilhar credenciais de acesso com terceiros.

4.4.2. O Usuário comprometa-se a notificar, imediatamente, a Plumaa em caso de suspeita de uso não autorizado de sua conta, pelos canais oficiais descritos na Cláusula 18.

## CLÁUSULA 5 — FUNCIONALIDADES DA PLATAFORMA

5.1. A Plataforma disponibiliza ao Usuário as seguintes funcionalidades principais:

a) **Busca e visualização de Profissionais**: consulta a perfis públicos contendo informações declaradas pelos próprios Profissionais (formação, especialização, abordagem, disponibilidade);

b) **Agendamento**: marcação de teleconsultas diretamente com o Profissional escolhido, conforme disponibilidade publicada na Plataforma;

c) **Realização de Teleconsulta**: acesso à videoconsulta por meio de integração com o **Google Meet**, não sendo exigida conta corporativa Google ou Gmail por parte do Usuário;

d) **Gestão de Agenda**: visualização de histórico de consultas, reagendamentos e cancelamentos nos termos destes Termos;

e) **Processamento de Pagamentos**: liquidação financeira dos serviços via integrador **Stripe**, por meio dos métodos disponíveis (cartão de crédito, débito, PIX e outros eventualmente habilitados).

5.2. **Modalidade Exclusivamente Remota.** No presente momento, a Plumaa **não disponibiliza modalidade presencial** de atendimento. Todos os atendimentos ocorrerão exclusivamente por videoconferência.

5.3. **Requisitos Técnicos.** É ônus exclusivo do Usuário dispor de:

a) conexão de internet estável e de banda larga;
b) dispositivo (computador, notebook, tablet ou smartphone) com câmera e microfone funcionais;
c) ambiente privativo, silencioso e adequado à teleconsulta, de modo a preservar o sigilo profissional e a ética do atendimento.

5.4. A Plumaa não se responsabiliza por indisponibilidade, atraso, falha ou baixa qualidade de atendimento decorrentes de deficiências no ambiente técnico do Usuário.

## CLÁUSULA 6 — PREÇOS, PAGAMENTOS E FATURAMENTO

6.1. **Precificação.** Os valores das teleconsultas são fixados e divulgados na Plataforma, podendo variar conforme o Profissional, a especialidade, o horário e eventuais campanhas promocionais. A Plumaa poderá, a seu exclusivo critério, alterar os valores a qualquer tempo, com efeitos a partir da publicação, não se aplicando retroativamente a consultas já confirmadas.

6.2. **Sistema de Pagamento.** O processamento financeiro é realizado pela **Stripe**, prestadora terceirizada independente. Ao efetuar pagamento, o Usuário declara ter lido e aceito os termos de uso e a política de privacidade da Stripe, disponíveis no site oficial da prestadora.

6.3. **Meios de Pagamento.** São aceitos os meios disponibilizados na Plataforma (cartão de crédito, débito, PIX e outros eventualmente habilitados). **É vedado qualquer arranjo financeiro direto entre Usuário e Profissional fora da Plataforma**, sob pena de suspensão ou encerramento do cadastro, sem prejuízo das sanções cabíveis.

6.4. **Emissão de Documento Fiscal.** A Plumaa emitirá o documento fiscal relativo à intermediação tecnológica e ao processamento de pagamento. Quando aplicável, o Profissional da Saúde emitirá nota fiscal de serviços (NFS-e) em nome do Usuário, conforme sua obrigação tributária individual.

6.5. **Direito de Arrependimento (art. 49 do CDC).** Tratando-se de contratação fora do estabelecimento comercial, o Usuário poderá exercer o direito de arrependimento no prazo de **7 (sete) dias corridos**, contados da contratação, **desde que o serviço ainda não tenha sido iniciado ou consumido**. Após a realização da teleconsulta, o serviço é considerado prestado e consumido, não havendo direito a reembolso a título de arrependimento.

## CLÁUSULA 7 — CANCELAMENTO, REAGENDAMENTO E REEMBOLSO

7.1. **Cancelamento pelo Usuário:**

a) **Com antecedência igual ou superior a 24 (vinte e quatro) horas** do horário agendado: o Usuário poderá cancelar ou reagendar a consulta sem custo, sendo o valor retornado como crédito no sistema ou, a critério da Plumaa, estornado ao meio de pagamento original.

b) **Com antecedência inferior a 24 (vinte e quatro) horas**: o cancelamento dependerá da anuência do Profissional. Caso o Profissional não aceite o cancelamento, **a sessão será considerada realizada** e o valor integral será devido, ressalvadas causas de força maior devidamente comprovadas.

7.2. **Cancelamento pelo Profissional:** Caso o Profissional cancele ou não compareça à sessão, o Usuário terá direito a (i) reagendamento sem custo adicional em horário disponível ou (ii) reembolso integral do valor pago, a sua escolha.

7.3. **Tolerância de Atraso.** A tolerância máxima de atraso, tanto pelo Usuário quanto pelo Profissional, é de **15 (quinze) minutos** contados do horário agendado. Ultrapassado esse prazo:

a) sendo o ausente o **Usuário**, a sessão será considerada **realizada** e o valor será devido integralmente ao Profissional;

b) sendo o ausente o **Profissional**, aplicar-se-á o disposto no item 7.2.

7.4. **Reembolsos.** Os reembolsos serão processados pela Plumaa, por meio do sistema de pagamento, nos prazos da operadora do cartão ou da instituição financeira do Usuário, descontadas eventuais taxas operacionais previamente informadas. A Plumaa envidará seus melhores esforços para célere resolução, mas não se responsabiliza por prazos e regras internas de terceiros processadores.

7.5. **Reembolso via Plano de Saúde.** O Usuário é o único responsável por verificar a elegibilidade de reembolso junto ao seu plano de saúde particular, solicitando o recibo/nota fiscal diretamente ao Profissional atendente. **A Plumaa não possui qualquer relação contratual com operadoras de planos de saúde.**

## CLÁUSULA 8 — CLUBE PLUMAA (PLANOS DE ASSINATURA)

8.1. **Definição.** O **Clube Plumaa** ("**Clube**") é a modalidade de assinatura recorrente, com cobrança mensal, por meio da qual o Usuário passa a ter acesso a um conjunto de benefícios previamente definidos no plano contratado, tais como créditos para teleconsultas, descontos, conteúdos e serviços correlatos ("**Benefícios**"). O Clube poderá ser contratado diretamente pelo Usuário ou disponibilizado por meio de empresa à qual o Usuário esteja vinculado, hipótese em que se aplicam, adicionalmente, as condições do item 8.9.

8.2. **Adesão e Cobrança.** A adesão ao Clube ocorre mediante conclusão do checkout na Plataforma, processado pela **Stripe**. A mensalidade será cobrada de forma **automática e recorrente**, no meio de pagamento cadastrado, a cada ciclo mensal contado da data de confirmação do primeiro pagamento. O checkout iniciado e não concluído no prazo indicado será automaticamente cancelado, sem qualquer ônus ao Usuário. A ausência de confirmação do pagamento de qualquer mensalidade implicará a **suspensão imediata dos Benefícios** até a regularização, sem prejuízo do cancelamento da assinatura em caso de inadimplência persistente.

8.3. **Natureza dos Benefícios.** Os Benefícios e créditos do Clube são **pessoais e intransferíveis**, vinculados à conta do Usuário assinante, e somente poderão ser utilizados nos serviços elegíveis indicados no plano contratado. O débito do crédito ocorre no momento da confirmação do agendamento. **Os Benefícios não possuem valor monetário, não são conversíveis em dinheiro, não geram direito a reembolso, saque ou troco e não podem ser cedidos, vendidos, doados ou compartilhados com terceiros.**

8.4. **Não Cumulatividade e Validade Mensal dos Créditos.** **Os créditos e Benefícios de renovação mensal disponibilizados pelo Clube são válidos exclusivamente dentro do ciclo mensal em que foram concedidos e NÃO SÃO CUMULATIVOS.** Os créditos não utilizados até o término do ciclo mensal **expiram automaticamente**, não sendo transferidos, somados ou acumulados para o ciclo seguinte, tampouco convertidos em desconto, saldo, crédito financeiro ou qualquer outra vantagem. A cada renovação da assinatura, mediante confirmação do pagamento da mensalidade, o saldo de créditos mensais é **reiniciado ao valor padrão do plano**, independentemente da quantidade utilizada no ciclo anterior. O Usuário reconhece que é de sua exclusiva responsabilidade acompanhar o saldo disponível na Plataforma e utilizar os créditos dentro do respectivo mês de vigência.

8.5. **Benefícios com Regra de Renovação Distinta.** Alguns Benefícios poderão ser expressamente identificados na Plataforma como de **"uso único"** (concedidos apenas uma vez, sem renovação) ou como **"sempre disponíveis"** (sem limite de quantidade durante a vigência da assinatura). A regra de renovação aplicável a cada Benefício será a indicada na Plataforma no momento da contratação, prevalecendo, na ausência de indicação, o disposto no item 8.4.

8.6. **Ausência de Saldo.** Esgotados os créditos do ciclo, o Usuário poderá continuar utilizando a Plataforma mediante pagamento avulso das teleconsultas, pelos valores vigentes, por meio dos métodos disponíveis, nos termos da Cláusula 6.

8.7. **Cancelamento e Reagendamento de Sessões Pagas com Crédito.** Às sessões agendadas com uso de crédito do Clube aplicam-se as regras da Cláusula 7. Quando devido o retorno do valor, o crédito será restituído ao saldo do Usuário **dentro do mesmo ciclo mensal**, permanecendo sujeito à validade e à não cumulatividade previstas no item 8.4. Sessões consideradas realizadas (não comparecimento ou cancelamento tardio não aceito pelo Profissional) consumirão o crédito correspondente.

8.8. **Cancelamento da Assinatura.** O Usuário poderá cancelar sua assinatura a qualquer tempo, por meio da Plataforma ou dos canais oficiais de atendimento (Cláusula 18). O cancelamento produzirá efeitos ao **final do ciclo mensal já pago**, permanecendo os Benefícios disponíveis até essa data. **Não haverá reembolso proporcional de mensalidade já paga**, ressalvado o direito de arrependimento previsto no item 6.5, exercido em até 7 (sete) dias corridos da primeira contratação e desde que nenhum Benefício tenha sido utilizado no período. Os créditos não utilizados até o término da assinatura serão definitivamente perdidos.

8.9. **Clube Vinculado a Empresa.** Quando o acesso ao Clube decorrer de vínculo do Usuário com empresa contratante da Plumaa, a elegibilidade, a duração e o conteúdo dos Benefícios dependerão das condições pactuadas entre a Plumaa e a referida empresa. O encerramento do vínculo do Usuário com a empresa, ou do contrato entre a empresa e a Plumaa, implicará a **cessação automática dos Benefícios**, sem direito a reembolso, indenização ou continuidade em favor do Usuário, o qual poderá, se desejar, contratar plano individual.

8.10. **Alterações e Descontinuidade.** A Plumaa poderá, a seu critério, alterar o conteúdo, a quantidade e o valor dos planos e Benefícios do Clube, mediante comunicação prévia ao Usuário com antecedência mínima de **30 (trinta) dias**, com efeitos a partir do ciclo mensal subsequente. Poderá, ainda, descontinuar planos, hipótese em que o Usuário será comunicado e não sofrerá nova cobrança. Caso não concorde com as alterações, o Usuário poderá cancelar a assinatura nos termos do item 8.8.

8.11. **Uso Indevido.** A utilização fraudulenta, abusiva ou em desacordo com estes Termos dos Benefícios do Clube, inclusive o compartilhamento de conta ou a tentativa de utilização de créditos em favor de terceiros, autorizará a Plumaa a suspender ou cancelar a assinatura, com a perda dos créditos remanescentes, sem prejuízo das medidas previstas na Cláusula 14.

## CLÁUSULA 9 — OBRIGAÇÕES E CONDUTA DO USUÁRIO

9.1. O Usuário obriga-se a:

a) utilizar a Plataforma de forma ética, lícita e de acordo com estes Termos e a legislação vigente;

b) comparecer às sessões agendadas, no horário previsto, em ambiente adequado;

c) tratar o Profissional com respeito, civilidade e urbanidade;

d) fornecer ao Profissional, no âmbito clínico, informações verídicas e completas sobre seu histórico e estado de saúde, ciente de que a omissão ou inveracidade pode prejudicar a qualidade do atendimento e afastar a responsabilidade do Profissional;

e) não gravar, reproduzir, divulgar ou compartilhar, por qualquer meio, áudio, vídeo, imagem ou conteúdo da teleconsulta sem a autorização expressa e por escrito do Profissional, sob pena de responsabilização civil e criminal;

f) manter atualizados seus dados cadastrais e de pagamento.

9.2. **Condutas Vedadas.** É expressamente vedado ao Usuário:

a) utilizar a Plataforma para fins ilícitos, fraudulentos, difamatórios, discriminatórios ou que violem direitos de terceiros;

b) criar mais de uma conta, utilizar dados de terceiros ou passar-se por outra pessoa;

c) agendar consultas em nome de terceiros, salvo autorização expressa da Plumaa e do Profissional;

d) **manter tratamento ou qualquer forma de atendimento com o Profissional captado via Plumaa fora do ambiente da Plataforma**, seja por pagamento direto, contato particular ou qualquer outro arranjo que contorne a intermediação da Plumaa;

e) praticar assédio, ofensa, ameaça, coação, discriminação ou qualquer forma de violência contra Profissionais ou colaboradores da Plumaa, seja por qual canal for (durante a consulta, e-mail, WhatsApp, redes sociais etc.);

f) utilizar bots, crawlers, scrapers, engenharia reversa, injeção de código, vírus ou qualquer forma de tentativa de invasão, modificação ou comprometimento da segurança da Plataforma;

g) reproduzir, redistribuir, sublicenciar ou comercializar qualquer conteúdo, marca, interface ou funcionalidade da Plataforma.

## CLÁUSULA 10 — RESPONSABILIDADES E LIMITAÇÕES

10.1. **Responsabilidade da Plumaa.** A Plumaa compromete-se, em regime de obrigação de meio (e não de resultado), a:

a) manter a Plataforma operacional e acessível, ressalvadas interrupções programadas, falhas de infraestrutura de terceiros (Google, Stripe, provedores de nuvem) ou eventos de força maior;

b) adotar medidas técnicas e administrativas razoáveis de segurança da informação e proteção de dados;

c) disponibilizar canal de atendimento para suporte, dúvidas e reclamações.

10.2. **Isenção de Responsabilidade — Atos Clínicos.** A Plumaa **não se responsabiliza**, sob qualquer fundamento, por:

a) qualidade, eficácia, adequação, tempestividade, resultado ou consequência de atos, condutas, diagnósticos, prescrições, orientações, omissões, imperícias, imprudências, negligências ou violações éticas cometidas por Profissionais da Saúde cadastrados;

b) veracidade ou atualidade das informações declaradas pelos Profissionais em seus perfis (formação, especialização, abordagem, experiência), sem prejuízo da verificação mínima do registro no conselho de classe;

c) eventos decorrentes do descumprimento, pelo Usuário, de orientações profissionais, dever de cuidado ou busca por atendimento presencial em situações de emergência;

d) indisponibilidades, atrasos ou falhas causados por serviços de terceiros (internet, operadoras de telefonia, Google Meet, Stripe, provedores de nuvem);

e) conteúdo, áudio, vídeo, prontuário ou qualquer registro da teleconsulta, já que **a Plumaa não monitora, grava ou armazena o conteúdo das sessões**;

f) vazamento, acesso indevido ou uso não autorizado de dados causado por falha atribuível ao Usuário (senha compartilhada, dispositivo comprometido, engenharia social, phishing etc.);

g) danos indiretos, lucros cessantes, perda de chance ou dano moral reflexo não diretamente atribuíveis à operação da Plataforma.

10.3. **Encaminhamento de Queixas Profissionais.** Reclamações relativas à conduta clínica, ética ou profissional deverão ser dirigidas:

a) ao próprio Profissional, para tentativa de resolução amigável;

b) ao respectivo Conselho Regional (CRP, CRM ou CRN) para apuração disciplinar;

c) cumulativamente, aos canais oficiais da Plumaa, para avaliação de medidas administrativas no âmbito da Plataforma.

10.4. **Teto de Responsabilidade.** Na máxima extensão permitida pela legislação aplicável, e sem prejuízo das normas cogentes do CDC, a responsabilidade eventual da Plumaa perante o Usuário estará limitada ao valor efetivamente pago pelo Usuário nas últimas 12 (doze) sessões na Plataforma, imediatamente anteriores ao evento que deu origem à reclamação.

## CLÁUSULA 11 — SIGILO, GRAVAÇÕES E PRIVACIDADE DAS CONSULTAS

11.1. A Plumaa adota arquitetura técnica que **não permite a gravação ou o monitoramento** do conteúdo (áudio ou vídeo) das teleconsultas por parte da plataforma. O sigilo profissional é dever do Profissional da Saúde, nos termos do Código de Ética de sua categoria.

11.2. Eventuais gravações realizadas pelo Profissional **deverão ser autorizadas expressamente pelo Usuário**, nos termos da legislação e das resoluções do conselho profissional aplicável. A Plumaa não detém nem tem acesso a tais gravações.

11.3. É **terminantemente vedada** a gravação da sessão pelo Usuário, total ou parcialmente, sem autorização expressa e por escrito do Profissional, sob pena de responsabilização civil e criminal, nos termos da legislação.

## CLÁUSULA 12 — PROTEÇÃO DE DADOS PESSOAIS (LGPD)

12.1. A Plumaa trata dados pessoais do Usuário em conformidade com a **Lei nº 13.709/2018 (LGPD)**, nos termos detalhados na **Política de Privacidade da Plumaa**, que constitui anexo obrigatório e integrante destes Termos.

12.2. O Usuário reconhece e concorda que, para a adequada prestação dos serviços, será necessário o tratamento de dados pessoais — inclusive **dados pessoais sensíveis de saúde** (art. 5º, II, da LGPD) —, mediante bases legais adequadas (execução de contrato, cumprimento de obrigação legal, consentimento, tutela da saúde e legítimo interesse, conforme o caso).

12.3. O Usuário poderá exercer os direitos de titular previstos no art. 18 da LGPD (confirmação, acesso, correção, anonimização, portabilidade, eliminação, revogação de consentimento, entre outros) por meio dos canais indicados na Política de Privacidade.

12.4. **Prontuário e Registros Clínicos.** A guarda, a portabilidade e o sigilo do prontuário e demais registros clínicos produzidos no atendimento são obrigação exclusiva do **Profissional da Saúde**, por força do Código de Ética aplicável e da legislação vigente.

## CLÁUSULA 13 — PROPRIEDADE INTELECTUAL

13.1. Todos os direitos de propriedade intelectual relativos à Plataforma — incluindo marca, logotipo, identidade visual, código-fonte, algoritmos, interface, design, conteúdo editorial, textos, imagens e demais elementos — são de titularidade exclusiva da Plumaa e/ou de seus licenciantes, sendo protegidos pelas Leis nº 9.279/1996 (Propriedade Industrial), nº 9.610/1998 (Direitos Autorais), nº 9.609/1998 (Software) e demais normas aplicáveis.

13.2. O acesso à Plataforma concede ao Usuário apenas uma **licença limitada, pessoal, intransferível, não exclusiva, revogável e gratuita** para uso das funcionalidades, nos termos destes Termos. Nenhuma outra cessão ou licença é concedida, expressa ou tacitamente.

13.3. É vedado ao Usuário reproduzir, modificar, traduzir, distribuir, sublicenciar, comercializar, fazer engenharia reversa, descompilar ou criar obras derivadas a partir de qualquer elemento da Plataforma.

## CLÁUSULA 14 — SUSPENSÃO E ENCERRAMENTO DA CONTA

14.1. **Encerramento pelo Usuário.** O Usuário poderá, a qualquer momento, solicitar o encerramento de sua conta por meio dos canais oficiais. O encerramento implicará desativação do perfil e tratamento dos dados pessoais conforme a Política de Privacidade, respeitados os prazos legais de guarda.

14.2. **Suspensão ou Encerramento pela Plumaa.** A Plumaa poderá suspender ou encerrar a conta do Usuário, imediata e unilateralmente, mediante notificação, nas seguintes hipóteses:

a) violação de qualquer disposição destes Termos ou da Política de Privacidade;

b) prática de ato fraudulento, ilícito, doloso ou que viole direito de terceiros;

c) prestação de informações falsas, inexatas ou desatualizadas no cadastro;

d) tentativa de agendamento em benefício de terceiro não autorizado;

e) tentativa de manter atendimento com Profissional captado via Plumaa fora da Plataforma;

f) conduta abusiva, ofensiva, discriminatória ou que ameace a integridade física, psíquica ou moral de Profissionais ou colaboradores;

g) inadimplência financeira;

h) ordem judicial ou requisição administrativa competente;

i) risco à integridade, segurança ou reputação da Plataforma ou de sua comunidade.

14.3. A suspensão ou encerramento não eximem o Usuário das obrigações financeiras pendentes, nem afastam eventual responsabilização civil ou criminal.

## CLÁUSULA 15 — DISPONIBILIDADE E MANUTENÇÃO

15.1. A Plumaa envidará seus melhores esforços para manter a Plataforma operacional em regime contínuo, **não oferecendo, contudo, garantia de disponibilidade ininterrupta**. Poderão ocorrer interrupções para manutenção programada, atualizações, correção de falhas ou por eventos de força maior.

15.2. Sempre que possível, a Plumaa comunicará, com antecedência razoável, interrupções programadas.

## CLÁUSULA 16 — FORÇA MAIOR E CASO FORTUITO

16.1. A Plumaa não será responsabilizada por descumprimento, atraso ou falha decorrentes de eventos de caso fortuito ou força maior, nos termos do art. 393 do Código Civil, incluindo, sem limitação: pandemias, desastres naturais, atos de autoridade pública, guerras, atentados, apagões, ataques cibernéticos, falhas em infraestruturas essenciais (internet, energia, telecomunicações) e indisponibilidade de prestadores terceirizados (Google, Stripe, provedores de nuvem).

## CLÁUSULA 17 — COMUNICAÇÕES E NOTIFICAÇÕES

17.1. As comunicações entre a Plumaa e o Usuário dar-se-ão, preferencialmente, por meio do e-mail cadastrado, pelo painel da Plataforma ou pelos canais oficiais indicados na Cláusula 18.

17.2. O Usuário compromete-se a manter seus dados de contato atualizados. Presumir-se-ão válidas as comunicações remetidas ao e-mail e telefone constantes do cadastro.

## CLÁUSULA 18 — CANAIS OFICIAIS DE ATENDIMENTO

18.1. Dúvidas, reclamações, solicitações de suporte, exercício de direitos do titular (LGPD) e demais comunicações oficiais deverão ser encaminhadas para:

- **E-mail:** contato@plumaa.com.br
- **WhatsApp oficial:** [número a ser confirmado]
- **Encarregado de Dados (DPO):** contato@plumaa.com.br

18.2. O prazo de resposta observará os critérios de razoabilidade e, quando aplicável, os prazos legais específicos (CDC, LGPD etc.).

## CLÁUSULA 19 — DISPOSIÇÕES GERAIS

19.1. **Independência das Cláusulas.** A eventual declaração de nulidade ou ineficácia de qualquer cláusula destes Termos não afetará a validade das demais, que permanecerão em pleno vigor.

19.2. **Não Renúncia.** A tolerância da Plumaa quanto ao descumprimento de qualquer obrigação pelo Usuário não constituirá renúncia ao direito de exigi-la a qualquer tempo.

19.3. **Cessão.** É vedado ao Usuário ceder ou transferir direitos e obrigações decorrentes destes Termos. A Plumaa poderá, independentemente de anuência do Usuário, ceder seus direitos e obrigações em caso de reorganização societária (fusão, cisão, incorporação ou transferência de ativos), mediante comunicação ao Usuário.

19.4. **Integralidade.** Estes Termos, em conjunto com a Política de Privacidade, constituem o acordo integral entre as partes quanto ao objeto, substituindo quaisquer tratativas anteriores.

## CLÁUSULA 20 — LEI APLICÁVEL E FORO

20.1. Estes Termos são regidos pelas leis da **República Federativa do Brasil**.

20.2. Fica eleito o **Foro da Comarca de São Paulo, Estado de São Paulo**, como competente para dirimir qualquer controvérsia oriunda destes Termos, com renúncia expressa a qualquer outro, por mais privilegiado que seja, ressalvadas as hipóteses de competência absoluta previstas em lei, notadamente em favor do consumidor (art. 101, I, do CDC).

20.3. As partes comprometem-se, previamente, à tentativa de composição amigável das controvérsias, por meio de mediação, antes do ajuizamento de qualquer demanda judicial.

## ACEITE ELETRÔNICO

Ao clicar em **"Li e aceito os Termos de Uso"**, ou ao efetivamente utilizar a Plataforma, o Usuário manifesta sua aceitação livre, informada, expressa e inequívoca a todas as disposições destes Termos e da Política de Privacidade da Plumaa.

**ARAUJO & SILVA LTDA** (nome fantasia: **PLUMAA**)
CNPJ 65.321.886/0001-10
R. Pais Leme, nº 215, Conj. 1713, Pinheiros, São Paulo/SP, CEP 05.424-150`;

const renderInlineBold = (text: string): ReactNode[] => {
	const parts = text.split(/(\*\*.*?\*\*)/g).filter(Boolean);
	return parts.map((part, idx) => {
		if (part.startsWith('**') && part.endsWith('**')) {
			return (
				<strong
					key={`strong-${idx}`}
					className="font-semibold text-gray-800"
				>
					{part.slice(2, -2)}
				</strong>
			);
		}
		return <Fragment key={`text-${idx}`}>{part}</Fragment>;
	});
};

const renderTermsContent = (content: string): ReactNode[] => {
	const lines = content.split('\n');
	const nodes: ReactNode[] = [];
	let i = 0;
	let key = 0;

	while (i < lines.length) {
		const line = lines[i];
		const trimmed = line.trim();

		if (!trimmed || trimmed === '---') {
			i += 1;
			continue;
		}

		if (trimmed.startsWith('## ')) {
			nodes.push(
				<h2
					key={`h2-${key++}`}
					className="pt-6 text-2xl font-semibold text-gray-800"
				>
					{trimmed.slice(3)}
				</h2>,
			);
			i += 1;
			continue;
		}

		if (trimmed.startsWith('- ')) {
			const items: string[] = [];
			while (i < lines.length && lines[i].trim().startsWith('- ')) {
				items.push(lines[i].trim().slice(2));
				i += 1;
			}
			nodes.push(
				<ul
					key={`ul-${key++}`}
					className="list-inside list-disc space-y-2 pl-4"
				>
					{items.map((item, idx) => (
						<li key={`li-${idx}`}>{renderInlineBold(item)}</li>
					))}
				</ul>,
			);
			continue;
		}

		const paragraphLines: string[] = [];
		while (i < lines.length) {
			const current = lines[i].trim();
			if (
				!current ||
				current === '---' ||
				current.startsWith('## ') ||
				current.startsWith('- ')
			) {
				break;
			}
			paragraphLines.push(lines[i]);
			i += 1;
		}

		nodes.push(
			<p key={`p-${key++}`} className="whitespace-pre-line">
				{renderInlineBold(paragraphLines.join('\n').trim())}
			</p>,
		);
	}

	return nodes;
};

const TermsOfUsePage = () => {
	return (
		<main className="mx-auto w-full max-w-3xl px-4 py-12 md:py-20">
			<Helmet>
				<title>Regras e Condições de Uso | Plumaa</title>
				<meta
					name="description"
					content="Consulte os Termos de Uso do ecossistema Plumaa. Diretrizes fundamentais para agendamento de consultas e utilização da nossa solução de saúde mental."
				/>

				<meta property="og:type" content="website" />
				<meta
					property="og:title"
					content="Regras e Condições de Uso | Plumaa"
				/>
				<meta
					property="og:description"
					content="Especialistas qualificados em psicologia e psiquiatria. Realize seu agendamento online com segurança."
				/>

				<meta
					property="og:image"
					content="https://res.cloudinary.com/dd5pbp2tb/image/upload/v1765924080/Banner_rxsb8s.png"
				/>
				<meta
					property="og:image:secure_url"
					content="https://res.cloudinary.com/dd5pbp2tb/image/upload/v1765924080/Banner_rxsb8s.png"
				/>

				<meta property="og:image:type" content="image/png" />
				<meta property="og:image:width" content="1200" />
				<meta property="og:image:height" content="630" />
			</Helmet>
			<h1 className="mb-8 text-2xl font-bold text-gray-900 md:text-3xl">
				TERMOS E CONDIÇÕES GERAIS DE USO — USUÁRIO
			</h1>
			<p className="mb-2 text-base text-zinc-600 md:text-lg">
				<strong>
					Plumaa — Plataforma Digital de Intermediação em Saúde
				</strong>
			</p>
			<p className="mb-8 font-semibold text-gray-800">
				Última atualização: 22 de abril de 2026
			</p>

			<div className="space-y-6 text-justify text-zinc-600">
				{renderTermsContent(TERMS_OF_USE_TEXT).map((node, idx) => (
					<Fragment key={`term-node-${idx}`}>{node}</Fragment>
				))}
			</div>
		</main>
	);
};

export default TermsOfUsePage;
